import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  Injector,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { ClassicPreset, NodeEditor } from 'rete';
import { AreaPlugin } from 'rete-area-plugin';
import { ConnectionPlugin, Presets as ConnectionPresets } from 'rete-connection-plugin';
import { AngularPlugin, Presets } from 'rete-angular-plugin/21';

export interface EntityFieldInfo {
  fieldName: string;
  displayName?: string;
  fieldType?: string;
  length?: number;
}

export interface ViewFieldInfo {
  fieldName: string;
  formula?: string;
}

export interface MappingConnection {
  fromEntityFieldName: string;
  toViewFieldName: string;
}

type Sockets = {
  field: ClassicPreset.Socket;
};

// Rete.js の型はバージョン差分が大きいので、まずは動作優先で緩く扱う
type Schemes = any;
type AreaExtra = any;

@Component({
  selector: 'app-mapping-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './mapping-editor.component.html',
  styleUrl: './mapping-editor.component.scss',
})
export class MappingEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly injector = inject(Injector);

  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  @Input({ required: true }) entityFields: EntityFieldInfo[] = [];
  @Input({ required: true }) viewFields: ViewFieldInfo[] = [];
  @Input() connections: MappingConnection[] = [];

  @Output() connectionsChange = new EventEmitter<MappingConnection[]>();

  protected readonly editorReady = signal(false);

  protected readonly filterForm = this.fb.group({
    entity: [''],
    view: [''],
  });

  private editor: NodeEditor<any> | null = null;
  private area: AreaPlugin<any, any> | null = null;
  private connectionPlugin: ConnectionPlugin<any, any> | null = null;

  private readonly socket: Sockets = {
    field: new ClassicPreset.Socket('field'),
  };

  private entityNode: ClassicPreset.Node | null = null;
  private viewNode: ClassicPreset.Node | null = null;

  async ngAfterViewInit(): Promise<void> {
    await this.initEditor();
    this.bindFilters();
    await this.rebuildGraph();
    this.editorReady.set(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editorReady()) return;

    if (changes['entityFields'] || changes['viewFields'] || changes['connections']) {
      // 入力が変わったら一度再構築（field数が多いので安全側に倒す）
      void this.rebuildGraph();
    }
  }

  ngOnDestroy(): void {
    // plugin側のdispose APIが公開されていないことがあるため、参照を破棄する
    this.editor = null;
    this.area = null;
    this.connectionPlugin = null;
    this.entityNode = null;
    this.viewNode = null;
  }

  private bindFilters(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged((a, b) => a.entity === b.entity && a.view === b.view),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.editorReady()) return;
        void this.rebuildGraph();
      });
  }

  private async initEditor(): Promise<void> {
    const container = this.containerRef.nativeElement;

    const editor = new NodeEditor<any>();
    const area = new AreaPlugin<any, any>(container);

    const render = new AngularPlugin<any, any>({ injector: this.injector });
    render.addPreset(Presets.classic.setup());

    const connection = new ConnectionPlugin<any, any>();
    connection.addPreset(ConnectionPresets.classic.setup());

    area.use(render);
    area.use(connection);

    // 接続作成/削除を捕捉して外部へ反映
    editor.addPipe((ctx) => {
      if (ctx.type === 'connectioncreate') {
        queueMicrotask(() => void this.enforceSingleConnectionPerViewInput());
        queueMicrotask(() => void this.emitConnections());
      }
      if (ctx.type === 'connectionremove') {
        queueMicrotask(() => void this.emitConnections());
      }
      return ctx;
    });

    this.editor = editor;
    this.area = area;
    this.connectionPlugin = connection;
  }

  private getFilteredEntityFields(): EntityFieldInfo[] {
    const q = (this.filterForm.get('entity')?.value ?? '').trim().toLowerCase();
    if (!q) return this.entityFields;
    return this.entityFields.filter((f) => {
      const a = f.fieldName.toLowerCase();
      const b = (f.displayName ?? '').toLowerCase();
      return a.includes(q) || b.includes(q);
    });
  }

  private getFilteredViewFields(): ViewFieldInfo[] {
    const q = (this.filterForm.get('view')?.value ?? '').trim().toLowerCase();
    if (!q) return this.viewFields;
    return this.viewFields.filter((f) => f.fieldName.toLowerCase().includes(q));
  }

  private async rebuildGraph(): Promise<void> {
    if (!this.editor || !this.area) return;

    // 既存を全消し（node数を最小に保つため、2ノード構成で作り直す）
    const existingConnections = this.safeGetConnections();
    for (const con of existingConnections) {
      await this.editor.removeConnection(con as any);
    }
    const existingNodes = this.safeGetNodes();
    for (const n of existingNodes) {
      await this.editor.removeNode(n as any);
    }

    const entityFields = this.getFilteredEntityFields();
    const viewFields = this.getFilteredViewFields();

    const entityNode = new ClassicPreset.Node('EntitySchema');
    for (const f of entityFields) {
      const label = f.displayName ? `${f.fieldName}（${f.displayName}）` : f.fieldName;
      entityNode.addOutput(f.fieldName, new ClassicPreset.Output(this.socket.field, label));
    }

    const viewNode = new ClassicPreset.Node('ViewSchema');
    for (const f of viewFields) {
      viewNode.addInput(f.fieldName, new ClassicPreset.Input(this.socket.field, f.fieldName));
    }

    await this.editor.addNode(entityNode as any);
    await this.editor.addNode(viewNode as any);

    // 位置（areaプラグインによりDOMで移動できる）
    await this.area.translate(entityNode.id as any, { x: 40, y: 80 });
    await this.area.translate(viewNode.id as any, { x: 520, y: 80 });

    this.entityNode = entityNode;
    this.viewNode = viewNode;

    // 入力で渡された接続を復元（フィルタで隠れている場合はスキップ）
    const connSet = new Set(this.connections.map((c) => `${c.fromEntityFieldName}=>${c.toViewFieldName}`));
    for (const key of connSet) {
      const [from, to] = key.split('=>');
      if (!from || !to) continue;
      if (!(entityNode.outputs as any)?.[from]) continue;
      if (!(viewNode.inputs as any)?.[to]) continue;

      const con = new ClassicPreset.Connection(entityNode, from, viewNode, to);
      await this.editor.addConnection(con as any);
    }

    await this.enforceSingleConnectionPerViewInput();
    this.emitConnections();
  }

  private safeGetNodes(): ClassicPreset.Node[] {
    try {
      return (this.editor as any)?.getNodes?.() ?? [];
    } catch {
      return [];
    }
  }

  private safeGetConnections(): any[] {
    try {
      return (this.editor as any)?.getConnections?.() ?? [];
    } catch {
      return [];
    }
  }

  private async enforceSingleConnectionPerViewInput(): Promise<void> {
    if (!this.editor || !this.viewNode) return;

    const connections = this.safeGetConnections();
    const byTarget = new Map<string, any[]>();

    for (const con of connections) {
      const targetNodeId = con.target;
      if (targetNodeId !== this.viewNode.id) continue;
      const targetInputKey = con.targetInput;
      const k = `${targetNodeId}:${String(targetInputKey)}`;
      const arr = byTarget.get(k) ?? [];
      arr.push(con);
      byTarget.set(k, arr);
    }

    for (const [, cons] of byTarget) {
      if (cons.length <= 1) continue;
      // 最後の1本だけ残す
      const keep = cons[cons.length - 1];
      for (const c of cons) {
        if (c === keep) continue;
        await this.editor.removeConnection(c);
      }
    }
  }

  private emitConnections(): void {
    if (!this.editor || !this.entityNode || !this.viewNode) return;

    const out: MappingConnection[] = [];
    const connections = this.safeGetConnections();
    for (const con of connections) {
      if (con.source !== this.entityNode.id) continue;
      if (con.target !== this.viewNode.id) continue;
      const from = String(con.sourceOutput);
      const to = String(con.targetInput);
      if (!from || !to) continue;
      out.push({ fromEntityFieldName: from, toViewFieldName: to });
    }

    // 安定順（UI同期の差分を小さくする）
    out.sort((a, b) => {
      const k1 = `${a.toViewFieldName}:${a.fromEntityFieldName}`;
      const k2 = `${b.toViewFieldName}:${b.fromEntityFieldName}`;
      return k1.localeCompare(k2);
    });

    this.connectionsChange.emit(out);
  }
}

